import { useEffect, useCallback, useRef } from 'react';
import { useStore } from '../../store';
import { useShallow } from 'zustand/react/shallow';
import type {
  KinnserClinicDataResponse,
  KinnserPersonInfo,
} from '../../services/api/types.gen';

interface UseKinnserClinicDataSharedOptions {
  agencySystemName: string | undefined;
  kinnserClinicId: string | null | undefined;
  enabled?: boolean;
}

interface UseKinnserClinicDataSharedReturn {
  clinicData: KinnserClinicDataResponse | null;
  loadingClinicData: boolean;
  clinics: KinnserPersonInfo[];
  loadingClinics: boolean;
  refetchClinicData: () => Promise<KinnserClinicDataResponse | null>;
}

/**
 * Shared hook for fetching and caching Kinnser clinic data.
 * Uses global Zustand store to cache data and prevent duplicate API calls
 * when navigating between SchedulingStep and CreateEpisodeStep.
 */
export function useKinnserClinicDataShared({
  agencySystemName,
  kinnserClinicId,
  enabled = true,
}: UseKinnserClinicDataSharedOptions): UseKinnserClinicDataSharedReturn {
  const {
    kinnserClinics,
    kinnserClinicsLoading,
    fetchKinnserClinics,
    fetchKinnserClinicData,
    getKinnserClinicData,
    isKinnserClinicDataLoading,
    isKinnserEnabled,

    // used for triggering re-renders after loading state changes
    // @ts-expect-error: intentionally unused - subscribed to trigger re-renders
    kinnserClinicDataLoading, // eslint-disable-line @typescript-eslint/no-unused-vars
  } = useStore(
    useShallow(state => ({
      kinnserClinics: state.kinnserClinics,
      kinnserClinicsLoading: state.kinnserClinicsLoading,
      fetchKinnserClinics: state.fetchKinnserClinics,
      fetchKinnserClinicData: state.fetchKinnserClinicData,
      getKinnserClinicData: state.getKinnserClinicData,
      isKinnserClinicDataLoading: state.isKinnserClinicDataLoading,
      kinnserClinicDataLoading: state.kinnserClinicDataLoading,
      isKinnserEnabled: state.isKinnserEnabled,
    }))
  );

  const effectiveEnabled = enabled && isKinnserEnabled();

  // Prevent repeated auto-retries for permanently failing agencies.
  const attemptedClinicsFetchAgencyRef = useRef<string | null>(null);

  useEffect(() => {
    attemptedClinicsFetchAgencyRef.current = null;
  }, [agencySystemName, effectiveEnabled]);

  // Fetch clinics list on mount (if not already cached)
  useEffect(() => {
    if (!effectiveEnabled || !agencySystemName) return;
    if (attemptedClinicsFetchAgencyRef.current === agencySystemName) return;
    if (kinnserClinics.length === 0 && !kinnserClinicsLoading) {
      attemptedClinicsFetchAgencyRef.current = agencySystemName;
      fetchKinnserClinics(agencySystemName);
    }
  }, [
    effectiveEnabled,
    agencySystemName,
    kinnserClinics.length,
    kinnserClinicsLoading,
    fetchKinnserClinics,
  ]);

  // Fetch clinic data when clinic ID changes
  useEffect(() => {
    if (!effectiveEnabled) return;
    if (agencySystemName && kinnserClinicId) {
      fetchKinnserClinicData(agencySystemName, kinnserClinicId);
    }
  }, [
    effectiveEnabled,
    agencySystemName,
    kinnserClinicId,
    fetchKinnserClinicData,
  ]);

  // Get current clinic data from cache
  const clinicData =
    effectiveEnabled && kinnserClinicId
      ? getKinnserClinicData(kinnserClinicId)
      : null;
  const loadingClinicData =
    effectiveEnabled && kinnserClinicId
      ? isKinnserClinicDataLoading(kinnserClinicId)
      : false;

  // Manual refetch function
  const refetchClinicData = useCallback(async () => {
    if (!effectiveEnabled) return null;
    if (!agencySystemName || !kinnserClinicId) return null;
    return fetchKinnserClinicData(agencySystemName, kinnserClinicId);
  }, [
    effectiveEnabled,
    agencySystemName,
    kinnserClinicId,
    fetchKinnserClinicData,
  ]);

  return {
    clinicData,
    loadingClinicData,
    clinics: effectiveEnabled ? kinnserClinics : [],
    loadingClinics: effectiveEnabled ? kinnserClinicsLoading : false,
    refetchClinicData,
  };
}
